import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@store/authStore";
import { getQAStatus, type QAStatus } from "@/api/admin";

const DISMISS_KEY = "qa_alert_dismissed";

export function QAAlertBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [status, setStatus] = useState<QAStatus | null>(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );

  useEffect(() => {
    if (!user) return;
    getQAStatus()
      .then(setStatus)
      .catch(() => {});
  }, [user]);

  if (dismissed || !status || status.status === "pass" || status.status === "none" || status.status === "running") {
    return null;
  }

  const isFail = status.status === "fail";

  return (
    <div
      className={`flex items-center justify-between px-4 py-2 text-sm ${
        isFail
          ? "bg-red-500/15 border-b border-red-500/30 text-red-400"
          : "bg-yellow-500/15 border-b border-yellow-500/30 text-yellow-400"
      }`}
    >
      <div className="flex items-center gap-2">
        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
        <span>
          {t("qaHealth.alertBanner")}
          {isFail && ` (${status.failures} ${t("qaHealth.fail").toLowerCase()})`}
          {!isFail && status.warnings > 0 && ` (${status.warnings} ${t("qaHealth.warn").toLowerCase()})`}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {user?.is_superadmin && (
          <button
            onClick={() => navigate("/qa-health")}
            className="text-xs underline hover:no-underline"
          >
            {t("qaHealth.viewDetails")}
          </button>
        )}
        <button
          onClick={() => {
            setDismissed(true);
            sessionStorage.setItem(DISMISS_KEY, "1");
          }}
          className="text-xs opacity-60 hover:opacity-100"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
